import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Endpoints } from '../../../../../apis';

type QueryError = { status?: number; message?: string } | null | undefined;

export function useInstanceQuery(instanceId: number) {
    const { t } = useTranslation('cloudComputeInstance');
    const navigate = useNavigate();
    const hasShownError = useRef(false);
    const hasRedirected = useRef(false);

    const query = useQuery({
        queryKey: ['api.v1.instances.get', instanceId],
        queryFn: async () => Endpoints.v1.Instances.getInstance(instanceId),
        enabled: instanceId > 0,
        retry: (failureCount, err) => (err as QueryError)?.status !== 404 && failureCount < 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        refetchInterval: 4000,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
    });

    const { data, isPending, error, refetch } = query;
    const errStatus = (error as QueryError)?.status;

    useEffect(() => {
        if (errStatus === 404 && !hasRedirected.current) {
            hasRedirected.current = true;
            notifications.show({
                title: t('notFound.title', 'Instance not found'),
                message: t(
                    'notFound.message',
                    'This instance no longer exists. Redirecting to the instance list.'
                ),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
            navigate('/cloud-compute', { replace: true });
        }
    }, [errStatus, navigate, t]);

    useEffect(() => {
        if (error && errStatus !== 404 && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('queryFailed.title'),
                message: t('queryFailed.instance'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, errStatus, t]);

    return { data, isPending, refetch };
}
